import React, { ReactElement, useMemo } from "react";
import styles from "./styles.module.scss";
import ArrowIcon from "./icons/arrowIcon";

interface PaginationProps {
    currentPage: number;
    totalCount: number;
    perPage: number;
    onPageChange: (page: number) => void;
    buildHref?: (page: number) => string;
}

const MAX_PAGES_WITHOUT_TRUNCATION = 7;
const LEADING_ELLIPSIS_THRESHOLD = 3;
const TRAILING_ELLIPSIS_OFFSET = 2;

function buildPageNumbers(
    currentPage: number,
    totalPages: number,
): (number | "…")[] {
    if (totalPages <= MAX_PAGES_WITHOUT_TRUNCATION) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | "…")[] = [1];
    if (currentPage > LEADING_ELLIPSIS_THRESHOLD) pages.push("…");
    for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
    ) {
        pages.push(i);
    }
    if (currentPage < totalPages - TRAILING_ELLIPSIS_OFFSET) pages.push("…");
    pages.push(totalPages);
    return pages;
}

export default function Pagination({
    currentPage,
    totalCount,
    perPage,
    onPageChange,
    buildHref,
}: PaginationProps): ReactElement | null {
    const totalPages = Math.ceil(totalCount / perPage);
    const isFirst = currentPage === 1;
    const isLast = currentPage === totalPages;

    const pageNumbers = useMemo(
        () => buildPageNumbers(currentPage, totalPages),
        [currentPage, totalPages],
    );

    if (totalPages <= 1) return null;

    const handleClick = (
        e: React.MouseEvent<HTMLAnchorElement>,
        page: number,
    ) => {
        if (
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey ||
            e.button !== 0
        ) {
            return;
        }
        e.preventDefault();
        onPageChange(page);
    };

    const renderControl = (
        page: number,
        {
            disabled,
            className,
            ariaLabel,
            ariaCurrent,
            children,
        }: {
            disabled?: boolean;
            className: string;
            ariaLabel: string;
            ariaCurrent?: "page";
            children: React.ReactNode;
        },
    ): ReactElement => {
        if (buildHref && !disabled) {
            return (
                <a
                    href={buildHref(page)}
                    className={className}
                    onClick={(e) => handleClick(e, page)}
                    aria-label={ariaLabel}
                    aria-current={ariaCurrent}
                >
                    {children}
                </a>
            );
        }

        return (
            <button
                type="button"
                className={className}
                onClick={() => onPageChange(page)}
                disabled={disabled}
                aria-label={ariaLabel}
                aria-current={ariaCurrent}
            >
                {children}
            </button>
        );
    };

    return (
        <nav className={styles.pagination} aria-label="Pagination">
            {renderControl(currentPage - 1, {
                disabled: isFirst,
                className: styles.arrowButton,
                ariaLabel: "Previous page",
                children: <ArrowIcon direction="left" disabled={isFirst} />,
            })}

            {pageNumbers.map((page, i) =>
                page === "…" ? (
                    <span key={`ellipsis-${i}`} className={styles.ellipsis}>
                        …
                    </span>
                ) : (
                    <React.Fragment key={page}>
                        {renderControl(page, {
                            className: `${styles.pageButton} ${
                                page === currentPage ? styles.activePage : ""
                            }`,
                            ariaLabel: `Page ${page}`,
                            ariaCurrent:
                                page === currentPage ? "page" : undefined,
                            children: page,
                        })}
                    </React.Fragment>
                ),
            )}

            {renderControl(currentPage + 1, {
                disabled: isLast,
                className: styles.arrowButton,
                ariaLabel: "Next page",
                children: <ArrowIcon direction="right" disabled={isLast} />,
            })}
        </nav>
    );
}
